/**
 * Checkout öncesi sepet doğrulaması (stok + kampanya hediyesi).
 *
 * Sepet sayfasındaki ödeme butonu (button[name="checkout"], form="cart") her
 * submit edildiğinde checkout'a geçmeden önce:
 * 1. Sepet satırlarının güncel stok durumu api-cart-stock-check section'ından
 *    okunur. Stoğu tükenen satırlar silinir, stoktan fazla adet içeren satırlar
 *    stok adedine indirilir (line item key ile tek atomik /cart/update.js).
 * 2. Kampanya hediyesi kurgusu güncel sepetle yeniden değerlendirilir; koşul
 *    artık sağlanmıyorsa hediye çıkar (HPAutoCampaignControl.settle).
 * 3. Checkout'a devam edilir. Sayfa yeniden render edilmez; checkout sepetin
 *    son halini gösterir. Sepet bu akışta değiştiyse form'daki updates[]
 *    alanları gönderilmez (DOM'daki adetler bayattır); değişmediyse submit eski
 *    akıştakiyle birebir aynıdır.
 *
 * Silme sonrası sepette ödenecek ürün kalmadıysa checkout'a gidilmez; sepet
 * yeniden render edilir ve müşteri bilgilendirilir.
 *
 * Kontrol yapılamazsa (ağ hatası, zaman aşımı, section yok) checkout'a eskisi
 * gibi devam edilir (fail-open). Paylaşılan checkout kilidi (hp-checkout-guard.js)
 * son sözü söyler: buton kilitliyken checkout'a geçilmez. Bu yüzden script,
 * kilidin submit dinleyicisinden SONRA yüklenmelidir (theme-script.liquid).
 *
 * @class HPCheckoutPreflight
 */
class HPCheckoutPreflight {
  /** @type {string} */
  static STOCK_SECTION_ID = 'api-cart-stock-check';

  /** @type {string} */
  static STOCK_DATA_ELEMENT_ID = 'api-cart-stock-check-data';

  /** @type {string} */
  static CAMPAIGN_SECTION_ID = 'api-campaign-calculate';

  /** Doğrulamanın üst süresi; aşılırsa checkout'a devam edilir (ms). */
  static TIMEOUT_MS = 10000;

  /** Süren sepet işinin (adet değişikliği, hediye mutasyonu) bitmesi için bekleme (ms). */
  static IDLE_WAIT_MS = 3000;

  /**
   * Adet değişikliği cart.js'te debounce (ON_CHANGE_DEBOUNCE_TIMER, 300 ms) ile
   * gönderilir; bu süre (+ pay) içinde değişiklik henüz istek olarak başlamamış sayılır (ms).
   */
  static QUANTITY_CHANGE_SETTLE_MS = 600;

  /** Doğrulama sürerken #main-cart'a eklenen class (ödeme butonu görünümü). */
  static BUSY_CLASS = 'cart-preflight-busy';

  /** Silme sonrası ödenecek ürün kalmadığında gösterilen mesaj. */
  static MESSAGE_ITEMS_REMOVED = 'Sepetinizdeki ürünlerin stoğu tükendiği için sepetinizden çıkarıldı.';

  /** @type {boolean} Doğrulama sürüyor mu (tekrarlanan submit'ler yok sayılır). */
  static _running = false;

  /** @type {boolean} Doğrulama sonrası yapılan kendi submit'imiz mi. */
  static _bypass = false;

  /** @type {boolean} Bu doğrulamada sepete yazıldı mı. */
  static _cartChanged = false;

  /** @type {boolean} Sepet değiştikten sonra checkout'a geçildi mi (bfcache dönüşü için). */
  static _leftAfterMutation = false;

  /** @type {number} Son adet input'u değişikliğinin zamanı (Date.now). */
  static _lastQuantityChangeAt = 0;

  /**
   * @param {number} ms
   * @returns {Promise<void>}
   */
  static sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * @template T
   * @param {Promise<T>} promise
   * @param {number} ms
   * @returns {Promise<T>}
   */
  static withTimeout(promise, ms) {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('timeout')), ms);
      promise.then(
        (value) => {
          clearTimeout(timer);
          resolve(value);
        },
        (error) => {
          clearTimeout(timer);
          reject(error);
        }
      );
    });
  }

  /**
   * Submit, sepet formunun ödeme butonundan mı geliyor? (Enter ile yapılan
   * örtük submit'te submitter formun varsayılan butonudur.)
   *
   * @param {SubmitEvent} event
   * @returns {boolean}
   */
  static isCheckoutSubmit(event) {
    const form = event.target;
    if (!form || form.nodeName !== 'FORM') return false;
    if (form.id !== 'cart' && !form.hasAttribute('data-cart-form')) return false;
    const submitter = event.submitter;
    return submitter
      ? submitter.name === 'checkout'
      : Boolean(form.elements && form.elements.namedItem('checkout'));
  }

  /**
   * Sepet formuna bağlı ödeme butonu. Form, butonun kendi form sahibinden
   * okunur: cart drawer'ın formu da id="cart" taşıdığından getElementById
   * DOM sırasına göre yanlış formu döndürebilir.
   *
   * @returns {HTMLButtonElement|null}
   */
  static findCheckoutButton() {
    return (
      Array.from(document.querySelectorAll('button[name="checkout"]')).find(
        (btn) =>
          btn.type === 'submit' && btn.form && (btn.form.id === 'cart' || btn.form.hasAttribute('data-cart-form'))
      ) || null
    );
  }

  /**
   * Doğrulama sürerken ödeme butonunu meşgul gösterir. Class, sepet
   * re-render'larında (#main-cart innerHTML değişimi) korunması için
   * #main-cart'ın kendisine eklenir.
   *
   * @param {boolean} busy
   */
  static setBusy(busy) {
    document.getElementById('main-cart')?.classList.toggle(HPCheckoutPreflight.BUSY_CLASS, busy);
    const guardClass = window.HPAutoBagControl?.GUARD_CLASS || 'cart-sync-disabled';
    document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      if (busy) {
        btn.setAttribute('aria-busy', 'true');
      } else if (!btn.classList.contains(guardClass)) {
        // Kilit butonu tutuyorsa aria-busy onundur.
        btn.removeAttribute('aria-busy');
      }
    });
  }

  /**
   * Sayfada sonuçlanmamış sepet işi var mı: debounce'u bekleyen ya da süren
   * adet değişikliği (cart.js isteği boyunca #main-cart'a cart__items--disabled
   * ekler) veya süren hediye mutasyonu. Input değeri ile varsayılan değer
   * karşılaştırılmaz: geri tuşunda tarayıcının geri yüklediği form değerleri
   * bekleyen değişiklik sanılırdı.
   *
   * @returns {boolean}
   */
  static hasPendingCartWork() {
    if (Date.now() - HPCheckoutPreflight._lastQuantityChangeAt < HPCheckoutPreflight.QUANTITY_CHANGE_SETTLE_MS) {
      return true;
    }
    if (document.getElementById('main-cart')?.classList.contains('cart__items--disabled')) return true;
    const campaign = window.HPAutoCampaignControl;
    return Boolean(campaign && typeof campaign.isBusy === 'function' && campaign.isBusy());
  }

  /**
   * Sonuçlanmamış sepet işinin bitmesini (en fazla IDLE_WAIT_MS) bekler.
   * Adet değişikliği debounce ile gönderildiğinden, adedi değiştirip hemen
   * ödemeye basan müşterinin değişikliği stok okumasından önce sepete yazılır.
   *
   * @returns {Promise<void>}
   */
  static async waitForCartIdle() {
    const deadline = Date.now() + HPCheckoutPreflight.IDLE_WAIT_MS;
    while (HPCheckoutPreflight.hasPendingCartWork() && Date.now() < deadline) {
      await HPCheckoutPreflight.sleep(100);
    }
  }

  /**
   * Stok verisini (ve varsa kampanya hesabını) tek Section Rendering isteğiyle çeker.
   *
   * @param {boolean} withCampaign
   * @returns {Promise<Object<string, string|null>>} section id → HTML
   */
  static async fetchSections(withCampaign) {
    const ids = [HPCheckoutPreflight.STOCK_SECTION_ID];
    if (withCampaign) ids.push(HPCheckoutPreflight.CAMPAIGN_SECTION_ID);
    const cartUrl = window.routes?.cart_url || '/cart';
    // Stok anlık değişir; tarayıcının eski yanıtı 304 ile kullanması engellenir.
    const res = await fetch(`${cartUrl}?sections=${ids.join(',')}`, { cache: 'no-store' });
    if (!res.ok) throw new Error(`sections request failed: ${res.status}`);
    return res.json();
  }

  /**
   * @param {string|null|undefined} sectionHtml
   * @returns {Array<Object>|null} Satır listesi; veri okunamazsa null
   */
  static parseStockItems(sectionHtml) {
    if (!sectionHtml || typeof sectionHtml !== 'string') return null;
    try {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const scriptEl = doc.getElementById(HPCheckoutPreflight.STOCK_DATA_ELEMENT_ID);
      if (!scriptEl?.textContent) return null;
      const data = JSON.parse(scriptEl.textContent.trim());
      return Array.isArray(data.items) ? data.items : null;
    } catch (e) {
      console.warn('hp-checkout-preflight: Failed to parse stock data', e);
      return null;
    }
  }

  /**
   * Stok verisinden /cart/update.js `updates` nesnesini üretir (line item key → adet).
   * - Satın alınamayan satır → 0 (silinir)
   * - Stok sınırlıysa aynı variant'ın satırları sırayla stoktan pay alır;
   *   payını aşan satır payına indirilir (pay 0 ise silinir)
   *
   * @param {Array<Object>|null} items
   * @returns {Object<string, number>}
   */
  static buildStockUpdates(items) {
    const updates = {};
    if (!Array.isArray(items)) return updates;

    const remainingByVariant = new Map();
    items.forEach((item) => {
      if (!item || !item.key) return;
      if (!item.available) {
        updates[item.key] = 0;
        return;
      }

      // Satın alınabilir görünen variant için 1'den küçük sınır çelişkilidir;
      // güvenilmez veriyle satır silinmez.
      const max = Number(item.max_quantity);
      if (item.max_quantity === null || !Number.isInteger(max) || max < 1) return;

      const variantKey = String(item.variant_id);
      const remaining = remainingByVariant.has(variantKey) ? remainingByVariant.get(variantKey) : max;
      const allowed = Math.max(0, Math.min(item.quantity, remaining));
      remainingByVariant.set(variantKey, remaining - allowed);
      if (allowed < item.quantity) updates[item.key] = allowed;
    });
    return updates;
  }

  /**
   * Stok düzeltmesini sepete yazar. Kampanya kontrolü varsa güncel kampanya
   * hesabı aynı yanıtta istenir.
   *
   * @param {Object<string, number>} updates
   * @param {boolean} withCampaign
   * @returns {Promise<Object>} Cart API yanıtı
   */
  static async applyStockUpdates(updates, withCampaign) {
    const body = { updates };
    if (withCampaign) {
      body.sections = HPCheckoutPreflight.CAMPAIGN_SECTION_ID;
      body.sections_url = window.location.pathname;
    }
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`cart update failed: ${res.status}`);
    return res.json();
  }

  /**
   * Sepette ödenecek (hediye/paket gibi muaf olmayan) ürün kaldı mı?
   *
   * @returns {Promise<boolean>}
   */
  static async cartHasPurchasableItems() {
    const cartUrl = window.routes?.cart_url || '/cart';
    const res = await fetch(`${cartUrl}.js`, { cache: 'no-store' });
    // Bilinmiyorsa checkout'a devam edilir (fail-open).
    if (!res.ok) return true;
    const cart = await res.json();
    const items = Array.isArray(cart.items) ? cart.items : [];
    const limits = window.HPCartLimits;
    if (!limits || typeof limits.isCartItemExempt !== 'function') return items.length > 0;
    return items.some((item) => !limits.isCartItemExempt(item));
  }

  /**
   * Stok ve kampanya hediyesi doğrulamasını yapar.
   *
   * @returns {Promise<{hasPurchasableItems: boolean}>}
   */
  static async validateCart() {
    await HPCheckoutPreflight.waitForCartIdle();

    const campaign = window.HPAutoCampaignControl;
    const withCampaign = Boolean(campaign && typeof campaign.settle === 'function');

    const sections = await HPCheckoutPreflight.fetchSections(withCampaign);
    const stockItems = HPCheckoutPreflight.parseStockItems(sections?.[HPCheckoutPreflight.STOCK_SECTION_ID]);
    const updates = HPCheckoutPreflight.buildStockUpdates(stockItems);
    const stockChanged = Object.keys(updates).length > 0;

    let campaignHtml = sections?.[HPCheckoutPreflight.CAMPAIGN_SECTION_ID];
    if (stockChanged) {
      HPCheckoutPreflight._cartChanged = true;
      const cart = await HPCheckoutPreflight.applyStockUpdates(updates, withCampaign);
      campaignHtml = cart?.sections?.[HPCheckoutPreflight.CAMPAIGN_SECTION_ID];
    }

    if (withCampaign) {
      const campaignData = campaignHtml ? campaign.parseCampaignCalculateData(campaignHtml) : null;
      // Stok değiştiyse hesap okunamasa da hediye kurgusu güncel sepetle değerlendirilir.
      if ((campaignData && campaign.responseNeedsMutation(campaignData)) || (stockChanged && !campaignData)) {
        HPCheckoutPreflight._cartChanged = true;
        await campaign.settle();
      }
    }

    if (!stockChanged) return { hasPurchasableItems: true };
    return { hasPurchasableItems: await HPCheckoutPreflight.cartHasPurchasableItems() };
  }

  /**
   * Checkout'a, ödeme butonunun kendisiyle formu yeniden submit ederek geçer;
   * böylece kilit (hp-checkout-guard.js) ve submit dinleyicileri (RTB House
   * startorder) normal akıştaki gibi çalışır. requestSubmit tıklama olayı
   * üretmez: butona bağlı tıklama takibi (GTM vb.) ikinci kez tetiklenmez.
   *
   * Sepet bu doğrulamada değiştiyse updates[] alanları yalnızca bu submit
   * boyunca devre dışı bırakılır (form verisi submit anında senkron
   * oluşturulur): DOM'daki adetler bayattır ve satır sırasıyla eşleştiğinden
   * silinen satırlardan sonra yanlış satırlara uygulanır. Sepet değişmediyse
   * submit eski akıştakiyle birebir aynıdır.
   */
  static continueToCheckout() {
    // Buton sepet re-render'larında yenilenebildiğinden burada yeniden aranır.
    const button = HPCheckoutPreflight.findCheckoutButton();
    const form = button?.form;
    // Kilit butonu tutuyorsa geçiş yapılmaz; kilit açılınca müşteri tekrar dener.
    if (!form || button.disabled) return;

    let quantityInputs = [];
    if (HPCheckoutPreflight._cartChanged) {
      HPCheckoutPreflight._leftAfterMutation = true;
      quantityInputs = Array.from(form.elements).filter((el) => el.name === 'updates[]' && !el.disabled);
    }
    quantityInputs.forEach((el) => {
      el.disabled = true;
    });
    HPCheckoutPreflight._bypass = true;
    try {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(button);
      } else {
        button.click();
      }
    } finally {
      HPCheckoutPreflight._bypass = false;
      quantityInputs.forEach((el) => {
        el.disabled = false;
      });
    }
  }

  /**
   * Ödenecek ürün kalmadığında: checkout yerine sepeti tazele ve bilgilendir.
   */
  static showCartAfterRemoval() {
    window.HPCartLimits?.showToast?.(HPCheckoutPreflight.MESSAGE_ITEMS_REMOVED);
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * Doğrulamayı çalıştırır ve sonuca göre checkout'a geçer ya da sepette kalır.
   *
   * @returns {Promise<void>}
   */
  static async run() {
    HPCheckoutPreflight._cartChanged = false;
    HPCheckoutPreflight.setBusy(true);
    let result = { hasPurchasableItems: true };
    try {
      result = await HPCheckoutPreflight.withTimeout(
        HPCheckoutPreflight.validateCart(),
        HPCheckoutPreflight.TIMEOUT_MS
      );
    } catch (err) {
      // Fail-open: doğrulama yapılamadı, checkout'a eskisi gibi devam edilir.
      console.warn('hp-checkout-preflight: Validation failed, continuing to checkout', err);
    } finally {
      HPCheckoutPreflight.setBusy(false);
    }

    if (!result.hasPurchasableItems) {
      HPCheckoutPreflight.showCartAfterRemoval();
      return;
    }
    HPCheckoutPreflight.continueToCheckout();
  }

  /**
   * Sepet formunun checkout submit'ini yakalar (capture fazı). Kilit submit'i
   * zaten engellediyse dokunmaz; aksi halde submit'i durdurup doğrulamayı başlatır.
   *
   * @param {SubmitEvent} event
   */
  static handleSubmit(event) {
    if (HPCheckoutPreflight._bypass) return;
    if (!HPCheckoutPreflight.isCheckoutSubmit(event)) return;
    if (event.defaultPrevented) return;

    // Durdurulan submit diğer dinleyicilere (RTB House startorder) ulaşmaz;
    // checkout'a geçişi doğrulama sonrası yapılan submit bildirir.
    event.preventDefault();
    event.stopPropagation();
    if (HPCheckoutPreflight._running) return;

    HPCheckoutPreflight._running = true;
    HPCheckoutPreflight.run()
      .catch((err) => console.error('hp-checkout-preflight: Preflight failed', err))
      .finally(() => {
        HPCheckoutPreflight._running = false;
      });
  }

  /**
   * bfcache'den dönüşte (checkout'tan geri tuşu): sepet bu akışta değiştiyse
   * bayat görünümü tazele.
   *
   * @param {PageTransitionEvent} event
   */
  static handlePageShow(event) {
    if (!event.persisted) return;
    HPCheckoutPreflight.setBusy(false);
    if (!HPCheckoutPreflight._leftAfterMutation) return;
    HPCheckoutPreflight._leftAfterMutation = false;
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  /**
   * Dinleyicileri kaydeder.
   */
  static init() {
    document.addEventListener('submit', HPCheckoutPreflight.handleSubmit, true);
    document.addEventListener(
      'change',
      (event) => {
        if (event.target?.name === 'updates[]') HPCheckoutPreflight._lastQuantityChangeAt = Date.now();
      },
      true
    );
    window.addEventListener('pageshow', HPCheckoutPreflight.handlePageShow);
  }
}

window.HPCheckoutPreflight = HPCheckoutPreflight;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => HPCheckoutPreflight.init());
} else {
  HPCheckoutPreflight.init();
}
